import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, Input, Table, Spinner, CardFooter } from 'reactstrap';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { RISK_COUNTRY_COLUMNS } from './constants';
import {
  fetchRiskCountries,
  updateRiskCountries,
} from '../../../store/apis/travelService';
import { useTableHook } from '../../../components/hooks';
import { fetchTableColumnValues } from '../../../store/apis/commonService';
import {
  TableSortDisplayArrow,
  FilterDropdown,
  TableFilterBubbles,
  TablePagination,
} from '../../../components/Common';

import './RiskCountry.scss';

function RiskCountry() {
  document.title = 'HR Risk Countries | Envision - Daikin';
  const previousDataRef = useRef([]);
  const [riskCountriesData, setRiskCountriesData] = useState([]);
  const queryClient = useQueryClient();

  // Table Hook
  const {
    data,
    columnData,
    pageRequest,
    setPageRequest,
    applyFilter,
    columnName,
    setColumnName,
    resetFilters,
    handleManualSort,
    checkColumnIfSorted,
    isLoadingData,
    clearSingleFilterValue,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      sortColumn: 'Description',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: fetchRiskCountries,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'riskCountries',
    columnKeyPrefix: 'riskCountriesColumns',
    tableName: 'RiskCountryEnquiry',
  });

  useEffect(() => {
    if (data?.data) {
      setRiskCountriesData(data?.data);
      previousDataRef.current = data?.data?.map(item => ({ ...item }));
    }
  }, [data]);

  const handleChange = (index, field, value) => {
    const updatedData = [...riskCountriesData];
    updatedData[index][field] = value;
    setRiskCountriesData(updatedData);
  };

  const renderTableBody = () => {
    if (!riskCountriesData || riskCountriesData.length === 0) {
      return (
        <tr>
          <td colSpan={RISK_COUNTRY_COLUMNS.length} className="text-center">
            No data available.
          </td>
        </tr>
      );
    }
    return riskCountriesData?.map((item, index) => (
      <tr key={item.id}>
        <td>{item?.Description}</td>
        <td>
          <Input
            type="checkbox"
            className="blue-checkbox"
            checked={item.Param1 === 'Y'}
            onChange={e =>
              handleChange(index, 'Param1', e.target.checked ? 'Y' : 'N')
            }
          />
        </td>
        <td>
          <Input
            placeholder="Enter Comments"
            value={item?.Comments || ''}
            onChange={e => handleChange(index, 'Comments', e.target.value)}
            className="external-filter-input"
          />
        </td>
      </tr>
    ));
  };

  const { mutate: saveRiskCountries, isPending: saveRiskCountriesPending } =
    useMutation({
      mutationFn: payload => updateRiskCountries(payload),
      onSuccess: () => {
        toast.success('Risk countries updated successfully.', {
          className: 'toast-success',
        });
        queryClient.invalidateQueries(['riskCountries']);
        previousDataRef.current = riskCountriesData.map(item => ({ ...item }));
      },
      onError: () => {
        toast.error('Failed to save risk countries. Please try again.', {
          className: 'toast-error',
        });
      },
    });

  const handleSave = updatedData => {
    const updateRiskCountryList = updatedData?.map(item => ({
      countryCode: item?.Code,
      isRiskCountry: item.Param1,
      riskComments: item.Comments,
    }));
    saveRiskCountries({ updateRiskCountryList });
  };

  const checkIfDataChanged = () =>
    JSON.stringify(riskCountriesData) !==
    JSON.stringify(previousDataRef.current);

  return (
    <div className="page-content risk-country-page">
      <Card className="risk-country-card mb-3">
        <CardBody className="p-0">
          <div className="p-3 d-flex flex-row align-items-center justify-content-between">
            <span className="fw-semibold fs-5">Risk Countries</span>
          </div>
          {pageRequest?.filters?.length > 0 && (
            <div className="my-2 px-2">
              <TableFilterBubbles
                allFilters={pageRequest?.filters || []}
                clearSingleFilterValue={clearSingleFilterValue}
                clearAllFilters={resetFilters}
              />
            </div>
          )}
          <div className="table-responsive risk-country-table-container">
            <Table className="custom-table align-middle text-nowrap risk-country-table">
              <thead>
                <tr>
                  {RISK_COUNTRY_COLUMNS?.map(column => (
                    <th key={column?.key} className="fw-semibold">
                      <div className="d-flex flex-row align-items-center justify-content-start gap-1">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => handleAutoSort(column?.key)}
                        >
                          {column?.columnName}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(column?.key)}
                        />
                        <FilterDropdown
                          handleSetColumnName={() => {
                            setColumnName(column?.key);
                          }}
                          setColumnName={setColumnName}
                          columnData={columnData}
                          applyFilter={applyFilter}
                          columnName={columnName}
                          pageRequest={pageRequest}
                          setPageRequest={setPageRequest}
                          handleManualSort={handleManualSort}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isLoadingData ? (
                  <tr>
                    <td
                      colSpan={RISK_COUNTRY_COLUMNS.length}
                      className="text-center"
                    >
                      <Spinner className="m-3" color="primary" />
                    </td>
                  </tr>
                ) : (
                  renderTableBody()
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="custom-btn"
          onClick={() => handleSave(riskCountriesData)}
          disabled={saveRiskCountriesPending || !checkIfDataChanged()}
        >
          {saveRiskCountriesPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default RiskCountry;
